/**
 * GoalsTable - Tabela de metas com progresso visual
 * 
 * Exibe lista de metas de vendedores com:
 * - Avatar e nome do vendedor
 * - Tipo de meta
 * - Período
 * - Valor alvo vs alcançado
 * - Barra de progresso
 * - Ações (editar, excluir)
 * 
 * @version 1.0.0
 * @author Sistema
 */

import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  TrendingUp,
  Hash,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { SellerGoal } from '@/hooks/useSellerGoals';

// ============================================================================
// UTILS - Funções auxiliares
// ============================================================================

/**
 * Formata valor monetário em BRL
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Retorna cor do progresso baseado na porcentagem
 */
function getProgressColor(percentage: number): string {
  if (percentage >= 100) return 'bg-emerald-500';
  if (percentage >= 75) return 'bg-sky-500';
  if (percentage >= 50) return 'bg-amber-500';
  return 'bg-rose-500';
}

/**
 * Retorna variante do badge baseado no status
 */
function getStatusBadge(percentage: number): { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' } {
  if (percentage >= 100) return { label: 'Atingida', variant: 'default' };
  if (percentage >= 75) return { label: 'Em progresso', variant: 'secondary' };
  if (percentage >= 50) return { label: 'Parcial', variant: 'outline' };
  return { label: 'Abaixo', variant: 'destructive' };
}

// ============================================================================
// PROPS
// ============================================================================

interface GoalsTableProps {
  /** Lista de metas a exibir */
  goals: SellerGoal[];
  /** Estado de carregamento */
  isLoading?: boolean;
  /** Callback para editar meta */
  onEdit?: (goal: SellerGoal) => void;
  /** Callback para excluir meta */
  onDelete?: (goal: SellerGoal) => void;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function GoalsTable({
  goals,
  isLoading,
  onEdit,
  onDelete,
}: GoalsTableProps) {
  // ============================================================================
  // LOADING STATE
  // ============================================================================

  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {[1, 2, 3, 4, 5].map(i => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="text-xs font-medium">Vendedor</TableHead>
          <TableHead className="text-xs font-medium">Tipo</TableHead>
          <TableHead className="text-xs font-medium">Workspaces</TableHead>
          <TableHead className="text-xs font-medium">Período</TableHead>
          <TableHead className="text-xs font-medium">Meta</TableHead>
          <TableHead className="text-xs font-medium">Alcançado</TableHead>
          <TableHead className="text-xs font-medium w-[180px]">Progresso</TableHead>
          <TableHead className="text-xs font-medium">Status</TableHead>
          <TableHead className="w-10" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {goals.map(goal => {
          const status = getStatusBadge(goal.achievement_percentage);
          const progressColor = getProgressColor(goal.achievement_percentage);
          const isRevenue = goal.goal_type === 'revenue';

          return (
            <TableRow
              key={goal.id}
              className="cursor-pointer hover:bg-muted/40 group"
            >
              {/* Vendedor */}
              <TableCell className="py-2">
                <div className="flex items-center gap-3">
                  <SmartAvatar
                    src={goal.seller_avatar_url}
                    name={goal.seller_name}
                    size="sm"
                    use3DFallback
                  />
                  <div className="flex flex-col">
                    <span className="font-medium text-sm">{goal.seller_name}</span>
                    {goal.seller_email && (
                      <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                        {goal.seller_email}
                      </span>
                    )}
                  </div>
                </div>
              </TableCell>

              {/* Tipo */}
              <TableCell className="py-2">
                <div className="flex items-center gap-1.5">
                  {isRevenue ? (
                    <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
                  ) : (
                    <Hash className="h-3.5 w-3.5 text-sky-500" />
                  )}
                  <span className="text-sm">
                    {isRevenue ? 'Faturamento' : 'Negócios'}
                  </span>
                </div>
              </TableCell>

              {/* Workspaces */}
              <TableCell className="py-2">
                {goal.workspace_names && goal.workspace_names.length > 0 ? (
                  <div className="flex flex-wrap gap-1 max-w-[200px]">
                    {goal.workspace_names.slice(0, 2).map((name, idx) => (
                      <Badge key={idx} variant="outline" className="text-[10px] px-1.5 py-0">
                        {name}
                      </Badge>
                    ))}
                    {goal.workspace_names.length > 2 && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                        +{goal.workspace_names.length - 2}
                      </Badge>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">Todos</span>
                )}
              </TableCell>

              {/* Período */}
              <TableCell className="py-2">
                <div className="text-sm">
                  <span>{format(new Date(goal.period_start + 'T00:00:00'), 'dd/MM/yy', { locale: ptBR })}</span>
                  <span className="text-muted-foreground mx-1">→</span>
                  <span>{format(new Date(goal.period_end + 'T00:00:00'), 'dd/MM/yy', { locale: ptBR })}</span>
                </div>
              </TableCell>

              {/* Valor da Meta */}
              <TableCell className="py-2">
                <span className="text-sm font-medium">
                  {isRevenue ? formatCurrency(goal.target_value) : goal.target_value}
                </span>
              </TableCell>

              {/* Valor Alcançado */}
              <TableCell className="py-2">
                <span className="text-sm">
                  {isRevenue ? formatCurrency(goal.achieved_value) : goal.achieved_value}
                </span>
              </TableCell>

              {/* Barra de Progresso */}
              <TableCell className="py-2">
                <div className="flex items-center gap-2">
                  <Progress
                    value={Math.min(goal.achievement_percentage, 100)}
                    className={cn('h-2 flex-1', progressColor)}
                  />
                  <span className="text-xs font-medium w-12 text-right">
                    {goal.achievement_percentage.toFixed(1)}%
                  </span>
                </div>
              </TableCell>

              {/* Status Badge */}
              <TableCell className="py-2">
                <Badge variant={status.variant} className="text-xs">
                  {status.label}
                </Badge>
              </TableCell>

              {/* Ações */}
              <TableCell className="py-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEdit?.(goal)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Editar
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onDelete?.(goal)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Excluir
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
